/*
 * The web app declares its own, narrower registry models — a `WorkLocation`
 * here has an `active` flag and no timezone, where the shared package's has
 * both. Importing the shared ones would compile and then fail at the boundary,
 * so these come from the service that owns them.
 *
 * Type-only, so the import is erased and no runtime cycle exists with the
 * module that imports this one's values.
 */
import type {
  CostCenter,
  Department,
  JobLevel,
  PayrollGroup,
  Position,
  Team,
  WorkLocation,
} from './directory';
import type { AuditActor } from './audit';
import { apiTransport } from './api-client';
import {
  apiCostCentreService,
  apiDepartmentService,
  apiJobLevelService,
  apiPayrollGroupService,
  apiPositionService,
  apiTeamService,
  apiWorkLocationService,
} from './registries.api';

/**
 * Organisation structure.
 *
 * Every mapper here exists because the API and the screens disagree about
 * naming in ways a cast would hide. `parentId` against `parentDepartmentId`,
 * `_count.employees` against `headcount`, a nested `department` object against
 * a flat `departmentName` — all of them compile as casts and render blank.
 *
 * The pattern is the same throughout: name the API's shape, write the mapping
 * out, and let a field the API does not send be an obvious empty rather than an
 * `undefined` the screen prints.
 */

export interface ApiDepartment {
  id: string;
  organizationId: string;
  code: string;
  name: string;
  description: string | null;
  parentId: string | null;
  headEmployeeId: string | null;
  costCenterId: string | null;
  isActive: boolean;
  _count?: { employees?: number };
  /** The head by name, joined by the server; `inScope` — the viewer may open their record. */
  /** `left`: the head has separated; the post is empty in all but name. */
  head?: {
    id: string;
    fullName: string;
    inScope: boolean;
    left?: boolean;
    /** A head working their notice: their last working day, `YYYY-MM-DD`. */
    leavingOn?: string | null;
  } | null;
}

function toDepartment(row: ApiDepartment): Department {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    headEmployeeId: row.headEmployeeId,
  };
}

export interface ApiTeam {
  id: string;
  organizationId: string;
  code: string;
  name: string;
  departmentId: string | null;
  department: { id: string; name: string } | null;
  leadEmployeeId: string | null;
  isActive: boolean;
  _count?: { employees?: number };
}

function toTeam(row: ApiTeam): Team {
  return { id: row.id, name: row.name, departmentId: row.departmentId ?? '' };
}

export interface ApiPosition {
  id: string;
  organizationId: string;
  code: string;
  title: string;
  departmentId: string | null;
  department: { id: string; name: string } | null;
  jobLevelId: string | null;
  jobLevel: { id: string; name: string } | null;
  isActive: boolean;
  _count?: { employees?: number };
}

function toPosition(row: ApiPosition): Position {
  return {
    id: row.id,
    title: row.title,
    departmentId: row.departmentId ?? '',
    jobLevelId: row.jobLevelId ?? '',
  };
}

export interface ApiWorkLocation {
  id: string;
  organizationId: string;
  code: string;
  name: string;
  kind: string;
  address: string | null;
  latitude: string | number | null;
  longitude: string | number | null;
  geofenceRadiusMeters: number | null;
  timezone: string | null;
  isActive: boolean;
}

/**
 * Coordinates arrive as Decimal strings.
 *
 * Latitude and longitude are stored to enough precision that a float would
 * lose metres, so the API sends them as strings. Converting to a number here is
 * safe — a map pin does not need sub-metre accuracy — but it happens once,
 * explicitly, rather than wherever a screen happens to need it.
 */
function toCoordinate(value: string | number | null): number | null {
  if (value === null) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toWorkLocation(row: ApiWorkLocation): WorkLocation {
  return {
    id: row.id,
    name: row.name,
    kind: row.kind as WorkLocation['kind'],
    address: row.address ?? '',
    latitude: toCoordinate(row.latitude),
    longitude: toCoordinate(row.longitude),
    geofenceRadiusMeters: row.geofenceRadiusMeters,
    active: row.isActive,
  };
}

export interface ApiNamedRecord {
  id: string;
  organizationId: string;
  code: string;
  name: string;
  isActive: boolean;
  rank?: number;
  departmentId?: string | null;
  payFrequency?: string;
}

export const apiOrganizationService = {
  async departments(): Promise<Department[]> {
    const rows = await apiDepartmentService.all();
    return rows.map(toDepartment);
  },

  async teams(): Promise<Team[]> {
    const rows = await apiTeamService.all();
    return rows.map(toTeam);
  },

  async positions(): Promise<Position[]> {
    const rows = await apiPositionService.all();
    return rows.map(toPosition);
  },

  async jobLevels(): Promise<JobLevel[]> {
    const rows = await apiJobLevelService.all();
    return rows.map((row) => ({ id: row.id, name: row.name, rank: row.rank ?? 0 }));
  },

  async costCenters(): Promise<CostCenter[]> {
    const rows = await apiCostCentreService.all();
    return rows.map((row) => ({
      id: row.id,
      code: row.code,
      name: row.name,
      departmentId: row.departmentId ?? '',
    }));
  },

  async payrollGroups(): Promise<PayrollGroup[]> {
    const rows = await apiPayrollGroupService.all();
    return rows.map((row) => ({
      id: row.id,
      code: row.code,
      name: row.name,
      payFrequency: row.payFrequency ?? 'monthly',
    }));
  },

  /**
   * Departments with the figures the overview shows.
   *
   * Written because the page calls this rather than `departments()`, and the
   * prototype's version was still answering: real department names beside
   * fictional headcounts, which is the mixed state hardest to spot. The names
   * matched, so nothing looked wrong — the totals were simply from a different
   * dataset.
   *
   * The counts come from the server's own `_count`, so they agree with the
   * directory by construction rather than by both sides counting separately.
   */
  async departmentOverview() {
    const rows = (await apiDepartmentService.all()) as (ApiDepartment & {
      _count?: { employees?: number; teams?: number };
    })[];

    /*
     * Heads by name, joined by the server on the list itself. This used to
     * read each head with GET /employees/:id, which a manager or department
     * head may not call for anyone outside their scope: every head read
     * "Unassigned" and each department logged a 403.
     */
    return rows.map((row) => ({
      ...toDepartment(row),
      /*
       * A head who has left heads nothing: the department counts as without
       * one, and says whose post it was ("Head left — none assigned").
       */
      headName: row.head && !row.head.left ? row.head.fullName : null,
      headLeftName: row.head?.left ? row.head.fullName : null,
      // Still heading it until their last working day, which is shown beside them.
      headLeavingOn: row.head && !row.head.left ? (row.head.leavingOn ?? null) : null,
      headcount: row._count?.employees ?? 0,
      /*
       * Active is the same figure as headcount here, deliberately.
       *
       * The list endpoint counts employees without splitting by employment
       * status. Reporting a smaller invented "active" number would understate
       * the department; reporting the honest total under both labels is
       * accurate for what the endpoint knows, and the directory answers the
       * status question properly.
       */
      activeCount: row._count?.employees ?? 0,
      teamCount: row._count?.teams ?? 0,
    }));
  },

  async workLocations(): Promise<WorkLocation[]> {
    /*
     * Inactive included. The page counts and filters inactive locations, and
     * the registry hides them by default — so a location switched off on this
     * screen vanished from it, and the only way back was the database.
     */
    const page = await apiWorkLocationService.list({ includeInactive: true, pageSize: 200 });
    return page.rows.map(toWorkLocation);
  },

  /*
   * Writes.
   *
   * These keep the prototype's signatures, audit actor included, because the
   * `{...mock, ...api}` selector only overrides identical names — and a write
   * missing here fell through to the prototype's empty store, where every
   * department and location "could not be found" without a request being
   * sent. The actor is accepted and never sent: the server resolves the
   * caller from the session and writes the audit entry itself.
   */

  async createDepartment(
    input: { code: string; name: string; headEmployeeId: string | null },
    _actor: AuditActor,
  ): Promise<Department> {
    return toDepartment(
      await apiDepartmentService.create({
        code: input.code,
        name: input.name,
        // The create schema takes an id or nothing; "no head" is omission.
        ...(input.headEmployeeId ? { headEmployeeId: input.headEmployeeId } : {}),
      }),
    );
  },

  async updateDepartment(
    id: string,
    changes: { name?: string; code?: string; headEmployeeId?: string | null },
    _actor: AuditActor,
  ): Promise<Department> {
    return toDepartment(await apiDepartmentService.update(id, changes));
  },

  /**
   * "Remove" is deactivation.
   *
   * Employment history points at departments, so the server has no delete; a
   * deactivated department leaves every list and picker and stays readable
   * behind the people who worked in it. The server refuses while anyone
   * current is still assigned, and that refusal is shown verbatim.
   */
  async deleteDepartment(id: string, _actor: AuditActor): Promise<void> {
    await apiDepartmentService.deactivate(id);
  },

  /**
   * Edit a location, activity included.
   *
   * One PATCH carries the lot — the server stamps deactivation itself and
   * treats a boundary change as a critical audit entry that needs a reason.
   * Coordinates and kind are not editable on this screen and are not sent.
   */
  async updateWorkLocation(
    id: string,
    changes: Partial<WorkLocation>,
    _actor: AuditActor,
    reason?: string,
  ): Promise<WorkLocation> {
    return toWorkLocation(
      await apiTransport().patch<ApiWorkLocation>(`/api/v1/work-locations/${id}`, {
        ...(changes.name !== undefined ? { name: changes.name } : {}),
        ...(changes.address !== undefined ? { address: changes.address } : {}),
        ...(changes.geofenceRadiusMeters !== undefined
          ? { geofenceRadiusMeters: changes.geofenceRadiusMeters }
          : {}),
        ...(changes.active !== undefined ? { isActive: changes.active } : {}),
        ...(reason?.trim() ? { reason: reason.trim() } : {}),
      }),
    );
  },
};
